import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

let recentCache = null;

const loadRecent = async () => {
  if (recentCache) return recentCache;
  try {
    const d = await api.getHistory();
    const seen = new Set();
    recentCache = (d.history || [])
      .map((h) => (h.query || '').trim())
      .filter((q) => q && !seen.has(q.toLowerCase()) && seen.add(q.toLowerCase()));
  } catch {
    recentCache = [];
  }
  return recentCache;
};

export const rememberSearch = (q) => {
  const v = (q || '').trim();
  if (!v || !recentCache) return;
  recentCache = [v, ...recentCache.filter((r) => r.toLowerCase() !== v.toLowerCase())].slice(0, 10);
};

// Suggerimenti mentre si scrive: ricerche recenti dell'utente + popolari
// (con numero di annunci). Campo vuoto: recenti + tendenze del momento.
export function useSearchAutocomplete(value, onPick) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const reqId = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const q = (value || '').trim().toLowerCase();
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      const recent = await loadRecent();
      let list;
      if (q.length < 2) {
        const trending = await api.getTrendingSearches(8).then((d) => d.trending || []).catch(() => []);
        const rec = recent.slice(0, 4).map((k) => ({ keyword: k, kind: 'recent' }));
        const taken = new Set(rec.map((r) => r.keyword.toLowerCase()));
        list = [...rec, ...trending
          .filter((t) => !taken.has(t.keyword.toLowerCase()))
          .slice(0, 6)
          .map((t) => ({ keyword: t.keyword, category: t.category, kind: 'trending' }))];
      } else {
        const popular = await api.searchAutocomplete(q, 8).then((d) => d.suggestions || []).catch(() => []);
        const rec = recent
          .filter((k) => k.toLowerCase().includes(q) && k.toLowerCase() !== q)
          .slice(0, 3)
          .map((k) => ({ keyword: k, kind: 'recent' }));
        const taken = new Set(rec.map((r) => r.keyword.toLowerCase()));
        list = [...rec, ...popular
          .filter((p) => !taken.has(p.keyword.toLowerCase()) && p.keyword.toLowerCase() !== q)
          .map((p) => ({ keyword: p.keyword, category: p.category, kind: 'popular' }))].slice(0, 8);
      }
      if (id === reqId.current) {
        setItems(list);
        setActive(-1);
      }
    }, q.length < 2 ? 0 : 120);
    return () => clearTimeout(timer);
  }, [value, open]);

  const pick = (keyword) => {
    setOpen(false);
    setActive(-1);
    rememberSearch(keyword);
    onPick(keyword);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' && items.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === 'ArrowUp' && items.length) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = open && active >= 0 ? items[active]?.keyword : (value || '').trim();
      if (chosen) pick(chosen);
    }
  };

  return {
    open: open && items.length > 0,
    items,
    active,
    query: (value || '').trim(),
    pick,
    setActive,
    inputProps: {
      // Non su focus: l'autofocus della home aprirebbe il menu senza richiesta
      onPointerDown: () => setOpen(true),
      onBlur: () => setTimeout(() => setOpen(false), 120),
      onKeyDown,
      onInput: () => setOpen(true),
      autoComplete: 'off',
      role: 'combobox',
      'aria-expanded': open && items.length > 0,
      'aria-autocomplete': 'list',
    },
  };
}
